/**
 * Map click handler hook.
 * Creates a placement marker on click and updates the map store.
 */

import { useEffect, useRef } from 'react';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { useMapStore } from '@/stores/map-store';
import { useDrawingStore } from '@/stores/drawing-store';

/** Attach click handlers to the map */
export function useMapClick(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const placementMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  useEffect(() => {
    const checkAndAttach = setInterval(() => {
      const map = mapRef.current;
      if (!map) return;
      clearInterval(checkAndAttach);

      const mode = modeRef.current;
      if (mode === '2d') {
        attachClickHandler2D(map as google.maps.Map, placementMarkerRef);
      } else {
        attachClickHandler3D(map as google.maps.maps3d.Map3DElement);
      }
    }, 100);

    return () => {
      clearInterval(checkAndAttach);
      removePlacementMarker(placementMarkerRef);
    };
  }, []);

  useEffect(() => {
    const unsubMode = useMapStore.subscribe((state, prev) => {
      if (state.viewMode !== prev.viewMode) {
        removePlacementMarker(placementMarkerRef);
      }
    });
    return unsubMode;
  }, []);
}

/** Attach click handler for 2D map */
function attachClickHandler2D(
  map: google.maps.Map,
  placementMarkerRef: React.RefObject<google.maps.marker.AdvancedMarkerElement | null>,
): void {
  map.addListener('click', (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    if (useDrawingStore.getState().activeTool) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    useMapStore.getState().setLastClick({ lng, lat });
    updatePlacementMarker2D(map, { lat, lng }, placementMarkerRef);
  });
}

/** Attach click handler for 3D map */
function attachClickHandler3D(map3d: google.maps.maps3d.Map3DElement): void {
  map3d.addEventListener('gmp-click', (e: Event) => {
    const clickEvent = e as google.maps.maps3d.LocationClickEvent;
    const position = clickEvent.position;
    if (!position) return;
    if (useDrawingStore.getState().activeTool) return;

    useMapStore.getState().setLastClick({ lng: position.lng, lat: position.lat });
  });
}

/** Update or create a placement marker at the clicked position */
function updatePlacementMarker2D(
  map: google.maps.Map,
  position: google.maps.LatLngLiteral,
  placementMarkerRef: React.RefObject<google.maps.marker.AdvancedMarkerElement | null>,
): void {
  removePlacementMarker(placementMarkerRef);

  const pinEl = createPlacementPin();
  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position,
    content: pinEl,
    title: 'New marker position',
  });

  (placementMarkerRef as React.MutableRefObject<google.maps.marker.AdvancedMarkerElement | null>).current = marker;
}

/** Create a styled placement pin element */
function createPlacementPin(): HTMLElement {
  const pin = document.createElement('div');
  pin.style.cssText = [
    'width:16px',
    'height:16px',
    'border-radius:50%',
    'background:#64b5f6',
    'border:2px solid #1976d2',
    'box-shadow:0 2px 6px rgba(0,0,0,0.3)',
  ].join(';');
  return pin;
}

/** Remove the current placement marker */
function removePlacementMarker(
  ref: React.RefObject<google.maps.marker.AdvancedMarkerElement | null>,
): void {
  if (ref.current) {
    ref.current.map = null;
    (ref as React.MutableRefObject<google.maps.marker.AdvancedMarkerElement | null>).current = null;
  }
}
